import { tokens } from '@/lib/tokens.js';
import { useState } from 'react';
import { useAdminRoles, useDeleteRole } from '../../hooks/use-admin-roles.js';

const s = {
  container: {
    backgroundColor: tokens.colors.bgCardDark,
    borderRadius: tokens.radius.card,
    overflow: 'hidden',
    boxShadow: '0 4px 6px -1px rgba(0,0,0,.1)',
  },
  header: {
    padding: '1rem 1.5rem',
    borderBottom: `1px solid ${tokens.colors.borderDark}`,
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  title: { fontSize: '1rem', fontWeight: 600, color: tokens.colors.textDark, margin: 0 },
  table: { width: '100%', borderCollapse: 'collapse' as const },
  th: {
    padding: '0.75rem 1rem',
    textAlign: 'left' as const,
    fontSize: '0.75rem',
    fontWeight: 600,
    color: tokens.colors.textMutedDark,
    textTransform: 'uppercase' as const,
    letterSpacing: '0.05em',
    borderBottom: `1px solid ${tokens.colors.borderDark}`,
    backgroundColor: tokens.colors.bgCardDark,
    position: 'sticky' as const,
    top: 0,
  },
  td: {
    padding: '0.75rem 1rem',
    fontSize: '0.875rem',
    color: tokens.colors.textDark,
    borderBottom: '1px solid rgba(55,65,81,0.5)',
    verticalAlign: 'middle' as const,
  },
  trOdd: { backgroundColor: 'rgba(17,24,39,0.3)' },
  btn: {
    backgroundColor: tokens.colors.primary,
    color: '#fff',
    border: 'none',
    borderRadius: tokens.radius.button,
    padding: '0.375rem 0.875rem',
    fontSize: '0.875rem',
    cursor: 'pointer',
    fontWeight: 500,
  },
  btnSm: {
    backgroundColor: 'transparent',
    color: tokens.colors.primary,
    border: `1px solid ${tokens.colors.primary}`,
    borderRadius: tokens.radius.button,
    padding: '0.25rem 0.625rem',
    fontSize: '0.8125rem',
    cursor: 'pointer',
    marginRight: '0.25rem',
  },
  btnSmDanger: {
    backgroundColor: 'transparent',
    color: tokens.colors.error,
    border: `1px solid ${tokens.colors.error}`,
    borderRadius: tokens.radius.button,
    padding: '0.25rem 0.625rem',
    fontSize: '0.8125rem',
    cursor: 'pointer',
  },
};

interface RolesListProps {
  tenantId: string;
  onSelect?: (roleId: string) => void;
  onCreateClick?: () => void;
}

export function RolesList({ tenantId, onSelect, onCreateClick }: RolesListProps) {
  const [page, setPage] = useState(1);
  const { data, isLoading, isError, refetch } = useAdminRoles(tenantId, { 'page-number': page });
  const deleteRole = useDeleteRole(tenantId);

  function handleDelete(roleId: string, roleName: string) {
    if (!window.confirm(`Delete role "${roleName}"? This cannot be undone.`)) return;
    deleteRole.mutate(roleId);
  }

  if (isLoading) {
    return (
      <output
        style={{
          display: 'block',
          ...s.container,
          padding: '2rem',
          textAlign: 'center',
          color: tokens.colors.textMutedDark,
        }}
        aria-label="Loading roles"
      >
        Loading roles…
      </output>
    );
  }

  if (isError) {
    return (
      <div style={{ ...s.container, padding: '2rem' }} role="alert">
        <p style={{ color: tokens.colors.error }}>Error loading roles</p>
        <button type="button" style={s.btn} onClick={() => void refetch()}>
          Retry
        </button>
      </div>
    );
  }

  const items = data?.items ?? [];
  const pagination = data?.pagination;

  return (
    <div style={s.container}>
      <div style={s.header}>
        <h2 style={s.title}>Roles</h2>
        <button type="button" style={s.btn} onClick={onCreateClick} aria-label="Create role">
          + New Role
        </button>
      </div>
      <div style={{ overflowX: 'auto' }}>
        <table style={s.table} aria-label="Roles list">
          <thead>
            <tr>
              <th style={s.th}>Code</th>
              <th style={s.th}>Name</th>
              <th style={s.th}>Default</th>
              <th style={s.th}>Members</th>
              <th style={s.th}>Created</th>
              <th style={s.th}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {items.length === 0 ? (
              <tr>
                <td
                  colSpan={6}
                  style={{ ...s.td, textAlign: 'center', color: tokens.colors.textMutedDark }}
                >
                  No roles found
                </td>
              </tr>
            ) : (
              items.map((role, idx) => (
                <tr key={role.id} style={idx % 2 === 1 ? s.trOdd : undefined}>
                  <td style={s.td}>
                    <span style={{ fontFamily: 'monospace', color: tokens.colors.textMutedDark }}>
                      {role.code}
                    </span>
                  </td>
                  <td style={s.td}>{role.name}</td>
                  <td style={s.td}>{role.is_default ? '✓' : ''}</td>
                  <td style={s.td}>{role.members_count ?? '—'}</td>
                  <td style={{ ...s.td, color: tokens.colors.textMutedDark }}>
                    {new Date(role.created_at).toLocaleDateString()}
                  </td>
                  <td style={s.td}>
                    <button
                      type="button"
                      style={s.btnSm}
                      onClick={() => onSelect?.(role.id)}
                      aria-label={`View ${role.name}`}
                    >
                      View
                    </button>
                    <button
                      type="button"
                      style={s.btnSmDanger}
                      onClick={() => handleDelete(role.id, role.name)}
                      aria-label={`Delete ${role.name}`}
                    >
                      Delete
                    </button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
      {pagination && (pagination.total_pages ?? 1) > 1 && (
        <div
          style={{
            padding: '0.75rem 1.5rem',
            display: 'flex',
            justifyContent: 'space-between',
            borderTop: `1px solid ${tokens.colors.borderDark}`,
            fontSize: '0.875rem',
            color: tokens.colors.textMutedDark,
          }}
        >
          <span>
            Page {pagination.page_number} of {pagination.total_pages}
          </span>
          <div style={{ display: 'flex', gap: '0.5rem' }}>
            <button
              type="button"
              style={{ ...s.btn, opacity: page <= 1 ? 0.5 : 1 }}
              disabled={page <= 1}
              onClick={() => setPage((p) => p - 1)}
              aria-label="Previous page"
            >
              Prev
            </button>
            <button
              type="button"
              style={{ ...s.btn, opacity: page >= pagination.total_pages ? 0.5 : 1 }}
              disabled={page >= pagination.total_pages}
              onClick={() => setPage((p) => p + 1)}
              aria-label="Next page"
            >
              Next
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
